import React, { useState, useEffect } from "react";
import {
  Brain,
  Search,
  Layers,
  Activity,
  Cpu,
  Shield,
  Sparkles,
  Target,
  AlertCircle,
} from "lucide-react";

// Define phases outside component to avoid recreation on each render
const phases = [
  {
    icon: Brain,
    title: "Initializing Your Analysis",
    messages: [
      "Loading psychological frameworks...",
      "Preparing ADHD transformation models...",
      "Activating pattern recognition...",
    ],
    progressStart: 0,
    progressEnd: 15,
  },
  {
    icon: Search,
    title: "Deep Pattern Recognition",
    messages: [
      "Analyzing your 15 responses...",
      "Identifying core identity patterns...",
      "Mapping your unique ADHD signature...",
    ],
    progressStart: 15,
    progressEnd: 30,
  },
  {
    icon: Layers,
    title: "Identity Archaeology",
    messages: [
      "Uncovering your authentic self...",
      "Identifying survival identities...",
      "Connecting childhood patterns to present...",
    ],
    progressStart: 30,
    progressEnd: 50,
  },
  {
    icon: Activity,
    title: "Belief System Analysis",
    messages: [
      "Exposing limiting belief patterns...",
      "Finding evidence that contradicts them...",
      "Generating empowering reframes...",
    ],
    progressStart: 50,
    progressEnd: 70,
  },
  {
    icon: Cpu,
    title: "Harmony Diagnostic",
    messages: [
      "Analyzing your 8 wellbeing dimensions...",
      "Identifying keystone areas...",
      "Mapping internal-external connections...",
    ],
    progressStart: 70,
    progressEnd: 85,
  },
  {
    icon: Shield,
    title: "Finalizing Your Portrait",
    messages: [
      "Synthesizing your Ikigai intersection...",
      "Creating your psychological portrait...",
      "Preparing your transformation preview...",
    ],
    progressStart: 85,
    progressEnd: 100,
  },
];

// Define funFacts outside component to avoid recreation on each render
const funFacts = [
  "💡 Your ADHD brain has 3x more creative connections than neurotypical brains",
  "🧠 People with ADHD often excel in crisis situations due to rapid pattern recognition",
  "⚡ Hyperfocus is actually a superpower—it just needs the right trigger",
  "🎯 Many successful entrepreneurs have ADHD: Richard Branson, Ingvar Kamprad, David Neeleman",
  "✨ Your brain isn't broken—it's running a different operating system",
  "🚀 ADHD brains are novelty-seeking, which is perfect for innovation",
  "💪 The same traits that cause struggle can become your greatest strengths",
  "🌟 Your report is being personalized based on YOUR unique answers",
];

const LoadingScreen = ({ firstName = "there" }) => {
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [subMessage, setSubMessage] = useState("");
  const [dots, setDots] = useState("");
  const [funFact, setFunFact] = useState(0);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Cycle through fun facts
  useEffect(() => {
    const interval = setInterval(() => {
      setFunFact((prev) => (prev + 1) % funFacts.length);
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  // Progress animation - 4 minutes (240 seconds)
  useEffect(() => {
    const duration = 240000; // 240 seconds (4 minutes)
    const increment = 100 / (duration / 100);

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const next = Math.min(prev + increment, 100);
        const newPhase = phases.findIndex(
          (phase) => next >= phase.progressStart && next < phase.progressEnd
        );
        if (newPhase !== -1 && newPhase !== currentPhase) {
          setCurrentPhase(newPhase);
        }
        return next;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, [currentPhase]);

  // Cycle through sub-messages
  useEffect(() => {
    if (phases[currentPhase]) {
      let messageIndex = 0;
      setSubMessage(phases[currentPhase].messages[0]);

      const messageInterval = setInterval(() => {
        messageIndex =
          (messageIndex + 1) % phases[currentPhase].messages.length;
        setSubMessage(phases[currentPhase].messages[messageIndex]);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [currentPhase]);

  const CurrentIcon = phases[currentPhase]?.icon || Brain;
  const timeRemaining = Math.max(0, Math.round((100 - progress) * 2.4));

  // Format time as minutes:seconds
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  return (
    <div className="min-h-screen gradient-bg py-6 px-4 flex items-center justify-center page-transition">
      <div className="w-full max-w-2xl">
        <div className="glass rounded-3xl shadow-2xl p-6 sm:p-8 border border-adhd-yellow/10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative inline-block mb-6">
              {/* Animated rings */}
              <div className="absolute inset-0 animate-ping opacity-20">
                <div className="w-20 h-20 mx-auto bg-adhd-yellow rounded-full"></div>
              </div>
              <div
                className="absolute inset-0 animate-ping opacity-10"
                style={{ animationDelay: "200ms" }}
              >
                <div className="w-20 h-20 mx-auto bg-adhd-orange rounded-full"></div>
              </div>

              {/* Icon container */}
              <div className="relative w-20 h-20 mx-auto bg-gradient-to-br from-adhd-yellow to-adhd-orange rounded-2xl flex items-center justify-center shadow-lg transform hover:rotate-6 transition-transform border-2 border-adhd-yellow/20">
                <CurrentIcon className="w-10 h-10 text-black animate-pulse" />
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-100 mb-2">
              {firstName}, your report is being created{dots}
            </h2>
            <p className="text-sm text-gray-300">
              Our AI is synthesizing your 5 days of insights into a personalized
              transformation portrait
            </p>
          </div>

          {/* Don't close warning */}
          <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-xs text-amber-200">
              <span className="font-bold">Please keep this tab open.</span> Your
              personalized report is being generated and will appear
              automatically when ready.
            </p>
          </div>

          {/* Current Phase */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base sm:text-lg font-bold text-gray-100">
                {phases[currentPhase]?.title}
              </h3>
              <span className="text-sm font-bold text-adhd-yellow bg-adhd-yellow/10 px-3 py-1 rounded-full border border-adhd-yellow/30">
                {Math.round(progress)}%
              </span>
            </div>

            {/* Progress Bar */}
            <div className="relative mb-3">
              <div className="w-full bg-gray-800/50 rounded-full h-3 overflow-hidden border border-gray-700">
                <div
                  className="h-full bg-gradient-to-r from-adhd-yellow via-adhd-orange to-adhd-yellow rounded-full transition-all duration-300 relative"
                  style={{
                    width: `${progress}%`,
                    backgroundSize: "200% 100%",
                    animation: "shimmer 2s infinite",
                  }}
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                </div>
              </div>

              {/* Phase indicators */}
              <div className="absolute top-0 left-0 w-full h-3 flex pointer-events-none">
                {phases.map((phase, index) => (
                  <div
                    key={index}
                    className="border-r-2 border-gray-900 last:border-r-0"
                    style={{
                      width: `${phase.progressEnd - phase.progressStart}%`,
                    }}
                  />
                ))}
              </div>
            </div>

            <p className="text-xs text-gray-400 mt-2 min-h-[16px] flex items-center gap-2">
              <Sparkles size={12} className="text-adhd-yellow flex-shrink-0" />
              <span>{subMessage}</span>
            </p>
          </div>

          {/* Phase Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {phases.map((phase, index) => {
              const PhaseIcon = phase.icon;
              const isActive = index === currentPhase;
              const isComplete = progress > phase.progressEnd;

              return (
                <div
                  key={index}
                  className={`p-3 rounded-xl border-2 transition-all ${
                    isComplete
                      ? "bg-adhd-green/10 border-adhd-green/30 shadow-sm"
                      : isActive
                      ? "bg-adhd-yellow/10 border-adhd-yellow/40 shadow-md transform scale-105 breathing"
                      : "glass-light border-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <PhaseIcon
                      className={`w-4 h-4 ${
                        isComplete
                          ? "text-adhd-green"
                          : isActive
                          ? "text-adhd-yellow animate-pulse"
                          : "text-gray-500"
                      }`}
                    />
                    <span
                      className={`text-xs font-semibold ${
                        isComplete
                          ? "text-adhd-green"
                          : isActive
                          ? "text-adhd-yellow"
                          : "text-gray-500"
                      }`}
                    >
                      Phase {index + 1}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fun fact carousel */}
          <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
            <p className="text-sm text-purple-200 text-center transition-all duration-500">
              {funFacts[funFact]}
            </p>
          </div>

          {/* Science badges */}
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-300 rounded-full text-xs font-semibold border border-blue-500/30">
              <Brain size={14} />
              <span>Depth Psychology</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-300 rounded-full text-xs font-semibold border border-purple-500/30">
              <Sparkles size={14} />
              <span>AI-Powered</span>
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 text-green-300 rounded-full text-xs font-semibold border border-green-500/30">
              <Shield size={14} />
              <span>Personalized</span>
            </span>
          </div>

          {/* Footer */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-adhd-yellow/5 border border-adhd-yellow/20 rounded-xl">
              <Target size={14} className="text-adhd-yellow" />
              <p className="text-xs text-gray-300 font-semibold">
                Estimated time remaining:{" "}
                <span className="text-adhd-yellow">
                  {formatTime(timeRemaining)}
                </span>
              </p>
            </div>
            <p className="text-[10px] text-gray-500">
              Deep psychological analysis typically takes 3-5 minutes
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: 200% center;
          }
          100% {
            background-position: -200% center;
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
